import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

export const emptyMemberFilter = () => ({ streamId: '', projectId: '', repositoryId: '' });

/** Search, scope filters, and tab navigation for the members page. */
export function useMembersFilterState(canManageInvites) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState(emptyMemberFilter);
  const [draftFilter, setDraftFilter] = useState(emptyMemberFilter);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeMembersTab, setActiveMembersTab] = useState('active');
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<any>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreMenuOpen]);

  const visibleMembersTabIds = useMemo(() => {
    const tabs = ['active', 'invites'];
    if (canManageInvites) tabs.push('requests');
    return tabs;
  }, [canManageInvites]);

  useEffect(() => {
    if (!visibleMembersTabIds.includes(activeMembersTab)) {
      setActiveMembersTab('active');
    }
  }, [activeMembersTab, visibleMembersTabIds]);

  const onMembersTabListKeyDown = useCallback(
    (e) => {
      const idx = visibleMembersTabIds.indexOf(activeMembersTab);
      if (idx < 0) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveMembersTab(visibleMembersTabIds[(idx + 1) % visibleMembersTabIds.length]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveMembersTab(
          visibleMembersTabIds[
            (idx - 1 + visibleMembersTabIds.length) % visibleMembersTabIds.length
          ]
        );
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveMembersTab(visibleMembersTabIds[0]);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveMembersTab(visibleMembersTabIds[visibleMembersTabIds.length - 1]);
      }
    },
    [activeMembersTab, visibleMembersTabIds]
  );

  const hasActiveScopeFilter = Boolean(
    memberFilter.repositoryId || memberFilter.projectId || memberFilter.streamId
  );

  const hasSearch = Boolean(debouncedSearch && String(debouncedSearch).trim());
  const hasListFilters = hasSearch || hasActiveScopeFilter;

  const closeFilterModal = () => {
    setDraftFilter({ ...memberFilter });
    setFilterModalOpen(false);
  };

  const applyFilter = () => {
    setMemberFilter({ ...draftFilter });
    setFilterModalOpen(false);
  };

  return {
    search,
    setSearch,
    debouncedSearch,
    memberFilter,
    setMemberFilter,
    draftFilter,
    setDraftFilter,
    filterModalOpen,
    setFilterModalOpen,
    closeFilterModal,
    applyFilter,
    activeMembersTab,
    setActiveMembersTab,
    moreMenuOpen,
    setMoreMenuOpen,
    moreMenuRef,
    visibleMembersTabIds,
    onMembersTabListKeyDown,
    hasActiveScopeFilter,
    hasListFilters,
  };
}

/** TanStack table instance: sorting, pagination, row navigation. */
export function useMembersDataTable({ members, columns, debouncedSearch, memberFilter }: any) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState([{ id: 'displayName', desc: false }]);
  const [pageInput, setPageInput] = useState('');

  const table = useReactTable({
    data: members,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 15 },
    },
  });

  useEffect(() => {
    table.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset page when result set or filters change
  }, [
    members.length,
    debouncedSearch,
    memberFilter.streamId,
    memberFilter.projectId,
    memberFilter.repositoryId,
  ]);

  const goToMember = (id) => {
    navigate(`/members/${id}`);
  };

  const handleRowActivate = (memberId, e) => {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    if (e.key === ' ') e.preventDefault();
    goToMember(memberId);
  };

  return {
    sorting,
    pageInput,
    setPageInput,
    table,
    goToMember,
    handleRowActivate,
  };
}

/**
 * Combined members table UI state (filter + data table).
 * Prefer useMembersFilterState + useMembersDataTable when queries sit between them.
 */
export function useMembersTableState({ members, columns, canManageInvites }: any) {
  const filter = useMembersFilterState(canManageInvites);
  const dataTable = useMembersDataTable({
    members,
    columns,
    debouncedSearch: filter.debouncedSearch,
    memberFilter: filter.memberFilter,
  });
  return { ...filter, ...dataTable };
}
